// ============================================================================
// TAYYOR MENYU KUTUBXONASI — ega "Tayyor ro'yxatdan qo'shish" orqali tanlaydi
//
// Qoidalar (egasi bilan kelishilgan):
//  - Narx yo'q: qo'shishda ega yozmasa — 0 (menyuda "Narx yo'q", kassada sotilmaydi).
//  - Retsept — faqat tarkib: har bir xom ashyo qatori miqdori 0 bilan keladi, ega o'zi kiritadi.
//    Tarkibdagi har bir xom ashyo skladga ham qo'shiladi (retseptda bor, skladda yo'q — bo'lmaydi).
//  - Kalitlar (key, code) HECH QACHON o'zgartirilmaydi: restoran menyusidagi mahsulot qaysi
//    kutubxona taomidan kelganini (va umumiy rasmini) shu kalit bilan biladi.
// Rasmlar platformada bir nusxada (menu-library bucket, library_images jadvali).
// ============================================================================

export type LibraryUnit = 'g' | 'ml' | 'dona';

export interface LibraryPack {
  key: string;
  name: string;
  description: string;
}

export interface LibraryCategory {
  key: string;
  pack: string;
  name: string;
  icon: string;
}

export interface LibraryMaterial {
  code: string;
  name: string;
  /** Sklad bo'limi (xom ashyo toifasi) */
  category: string;
  unit: LibraryUnit;
  packName?: string;
  packSize?: number;
}

export interface LibraryItem {
  key: string;
  category: string;
  /** To'liq nom (kassada ko'rinadi): "Lavash mol go'shtli mini" */
  name: string;
  /** O'lchamlari bor taomlar bir guruhda: "Lavash mol go'shtli" — "Mini / Standart / Katta" */
  group?: string;
  variant?: string;
  /** Tarkib: xom ashyo kodlari (miqdori 0 bilan qo'shiladi) */
  materials: readonly string[];
}

export const LIBRARY_PACKS = [
  { key: 'shaurma', name: 'Shaurma va lavash', description: 'Lavash, shaurma, donar, tvister' },
  { key: 'burger', name: 'Burger, hot-dog, chicken', description: 'Burgerlar, hot-doglar, tovuq taomlari' },
  { key: 'gazak', name: 'Gazak, salat, sous', description: 'Kartoshka fri, gazaklar, salatlar, souslar' },
  { key: 'ichimlik', name: 'Ichimliklar va desert', description: 'Sovuq va issiq ichimliklar, desertlar' },
  { key: 'milliy', name: 'Milliy taomlar', description: 'Osh, lag‘mon, sho‘rvalar, shashlik, somsa va non' },
  { key: 'bar', name: 'Bar va kalyan', description: 'Pivo, kokteyllar, kalyan' },
] as const satisfies readonly LibraryPack[];

export const LIBRARY_CATEGORIES = [
  { key: 'lavash', pack: 'shaurma', name: 'Lavash', icon: '🌯' },
  { key: 'shaurma', pack: 'shaurma', name: 'Shaurma', icon: '🥙' },
  { key: 'donar', pack: 'shaurma', name: 'Donar', icon: '🍖' },
  { key: 'tvister', pack: 'shaurma', name: 'Tvister va sendvich', icon: '🥪' },
  { key: 'burger', pack: 'burger', name: 'Burger', icon: '🍔' },
  { key: 'hotdog', pack: 'burger', name: 'Hot-dog', icon: '🌭' },
  { key: 'chicken', pack: 'burger', name: 'Chicken', icon: '🍗' },
  { key: 'gazak', pack: 'gazak', name: 'Kartoshka va gazak', icon: '🍟' },
  { key: 'salat', pack: 'gazak', name: 'Salatlar', icon: '🥗' },
  { key: 'sous', pack: 'gazak', name: 'Souslar', icon: '🥫' },
  { key: 'sovuq', pack: 'ichimlik', name: 'Sovuq ichimliklar', icon: '🥤' },
  { key: 'issiq', pack: 'ichimlik', name: 'Issiq ichimliklar', icon: '☕' },
  { key: 'desert', pack: 'ichimlik', name: 'Desert', icon: '🍨' },
  { key: 'milliy', pack: 'milliy', name: 'Milliy taomlar', icon: '🍛' },
  { key: 'shorva', pack: 'milliy', name: 'Sho‘rvalar', icon: '🍲' },
  { key: 'shashlik', pack: 'milliy', name: 'Shashliklar', icon: '🍢' },
  { key: 'non_somsa', pack: 'milliy', name: 'Non va somsa', icon: '🫓' },
  { key: 'pivo', pack: 'bar', name: 'Pivo', icon: '🍺' },
  { key: 'kokteyl', pack: 'bar', name: 'Kokteyllar', icon: '🍹' },
  { key: 'kalyan', pack: 'bar', name: 'Kalyan', icon: '💨' },
] as const satisfies readonly LibraryCategory[];

const m = (code: string, name: string, category: string, unit: LibraryUnit, packName?: string, packSize?: number): LibraryMaterial =>
  packName ? { code, name, category, unit, packName, packSize } : { code, name, category, unit };

export const LIBRARY_MATERIALS: readonly LibraryMaterial[] = [
  // Go'sht
  m('mol_goshti', 'Mol go‘shti', 'go‘sht', 'g', 'kg', 1000),
  m('tovuq_goshti', 'Tovuq go‘shti', 'go‘sht', 'g', 'kg', 1000),
  m('tovuq_file', 'Tovuq filesi', 'go‘sht', 'g', 'kg', 1000),
  m('tovuq_qanot', 'Tovuq qanoti', 'go‘sht', 'dona'),
  m('tovuq_oyoq', 'Tovuq oyoqchasi', 'go‘sht', 'dona'),
  m('qoy_goshti', 'Qo‘y go‘shti', 'go‘sht', 'g', 'kg', 1000),
  m('jigar', 'Jigar', 'go‘sht', 'g', 'kg', 1000),
  m('qiyma', 'Qiyma', 'go‘sht', 'g', 'kg', 1000),
  m('dumba', 'Dumba yog‘i', 'go‘sht', 'g', 'kg', 1000),
  m('burger_kotlet', 'Burger kotleti (mol)', 'go‘sht', 'dona', 'pachka', 10),
  m('kalbasa', 'Kolbasa', 'go‘sht', 'g', 'kg', 1000),
  m('sosiska', 'Sosiska (oddiy)', 'go‘sht', 'dona', 'karobka', 24),
  m('sosiska_kanada', 'Sosiska (Kanada)', 'go‘sht', 'dona', 'karobka', 24),
  m('nagets', 'Nagets (muzlatilgan)', 'go‘sht', 'dona'),
  // Non
  m('lavash_non', 'Lavash noni', 'non', 'dona', 'pachka', 20),
  m('pita', 'Pita', 'non', 'dona', 'pachka', 10),
  m('burger_non', 'Burger noni', 'non', 'dona', 'pachka', 4),
  m('fransuz_non', 'Fransuzcha hot-dog noni', 'non', 'dona'),
  m('tost_non', 'Tost noni', 'non', 'dona'),
  m('tandir_non', 'Tandir non', 'non', 'dona'),
  // Sabzavot va ko'kat
  m('karam', 'Karam', 'sabzavot', 'g', 'kg', 1000),
  m('pomidor', 'Pomidor', 'sabzavot', 'g', 'kg', 1000),
  m('bodring', 'Bodring', 'sabzavot', 'g', 'kg', 1000),
  m('tuzlangan_bodring', 'Tuzlangan bodring', 'sabzavot', 'g', 'banka', 700),
  m('piyoz', 'Piyoz', 'sabzavot', 'g', 'kg', 1000),
  m('sabzi', 'Sabzi', 'sabzavot', 'g', 'kg', 1000),
  m('sabzi_salat', 'Sabzi salat (koreyscha)', 'sabzavot', 'g', 'kg', 1000),
  m('salat_bargi', 'Salat bargi', 'sabzavot', 'g', 'kg', 1000),
  m('bulgor_qalampir', 'Bulg‘or qalampiri', 'sabzavot', 'g', 'kg', 1000),
  m('achchiq_qalampir', 'Achchiq qalampir', 'sabzavot', 'g', 'kg', 1000),
  m('zaytun', 'Zaytun', 'sabzavot', 'g', 'banka', 300),
  m('kartoshka', 'Kartoshka', 'sabzavot', 'g', 'kg', 1000),
  m('kokat', 'Ko‘kat', 'sabzavot', 'g'),
  m('laym', 'Laym', 'sabzavot', 'dona'),
  m('kartoshka_fri', 'Kartoshka fri (muzlatilgan)', 'sabzavot', 'g', 'qop', 2500),
  m('limon', 'Limon', 'sabzavot', 'dona'),
  m('yalpiz', 'Yalpiz', 'sabzavot', 'g'),
  // Sut mahsulotlari va tuxum
  m('pishloq', 'Pishloq', 'sut', 'g', 'kg', 1000),
  m('brinza', 'Brinza', 'sut', 'g', 'kg', 1000),
  m('parmezan', 'Parmezan', 'sut', 'g'),
  m('tuxum', 'Tuxum', 'sut', 'dona', 'karobka', 30),
  m('sut', 'Sut', 'sut', 'ml', 'litr', 1000),
  m('muzqaymoq', 'Muzqaymoq', 'sut', 'g', 'kg', 1000),
  // Souslar
  m('oq_sous', 'Oq sous (mayonez)', 'sous', 'g', 'banka', 1000),
  m('tomat_sous', 'Tomat sous', 'sous', 'g', 'banka', 1000),
  m('ketchup', 'Ketchup', 'sous', 'g', 'banka', 1000),
  m('pishloq_sousi', 'Pishloq sousi', 'sous', 'g', 'butilka', 1000),
  m('chili_sous', 'Chili sous', 'sous', 'g', 'banka', 1000),
  m('barbekyu_sous', 'Barbekyu sous', 'sous', 'g', 'banka', 1000),
  m('sarimsoq_sous', 'Sarimsoqli sous', 'sous', 'g', 'banka', 1000),
  m('sezar_sous', 'Sezar sousi', 'sous', 'g', 'banka', 1000),
  // Quruq mahsulotlar va yog'
  m('guruch', 'Guruch', 'quruq', 'g', 'kg', 1000),
  m('un', 'Un', 'quruq', 'g', 'qop', 50000),
  m('mosh', 'Mosh', 'quruq', 'g', 'kg', 1000),
  m('noxat', 'No‘xat', 'quruq', 'g', 'kg', 1000),
  m('panirovka', 'Panirovka', 'quruq', 'g', 'kg', 1000),
  m('suxari', 'Suxari', 'quruq', 'g', 'kg', 1000),
  m('yog_fri', 'Yog‘ (fri uchun)', 'quruq', 'ml', 'litr', 1000),
  m('zaytun_moyi', 'Zaytun moyi', 'quruq', 'ml', 'litr', 1000),
  m('shakar', 'Shakar', 'quruq', 'g', 'kg', 1000),
  m('qora_choy', 'Qora choy (paket)', 'quruq', 'dona', 'karobka', 100),
  m('kok_choy', 'Ko‘k choy', 'quruq', 'g', 'pachka', 100),
  m('kofe_31', 'Kofe 3 in 1 (paket)', 'quruq', 'dona', 'pachka', 20),
  m('kofe_qora', 'Qora kofe (paket)', 'quruq', 'dona', 'pachka', 20),
  m('kofe_don', 'Kofe (don)', 'quruq', 'g', 'kg', 1000),
  m('kakao', 'Kakao', 'quruq', 'g'),
  m('kompot', 'Kompot (tayyorlangan)', 'quruq', 'ml', 'litr', 1000),
  // Bir martalik idishlar
  m('stakan', 'Bir martalik stakan', 'idish', 'dona', 'pachka', 50),
  m('sous_idish', 'Sous idishi', 'idish', 'dona', 'pachka', 100),
  // Tayyor ichimliklar (dona)
  m('cola_05', 'Coca-Cola 0,5 L', 'ichimlik', 'dona', 'blok', 12),
  m('cola_1', 'Coca-Cola 1 L', 'ichimlik', 'dona', 'blok', 6),
  m('cola_15', 'Coca-Cola 1,5 L', 'ichimlik', 'dona', 'blok', 6),
  m('fanta_05', 'Fanta 0,5 L', 'ichimlik', 'dona', 'blok', 12),
  m('fanta_1', 'Fanta 1 L', 'ichimlik', 'dona', 'blok', 6),
  m('fanta_15', 'Fanta 1,5 L', 'ichimlik', 'dona', 'blok', 6),
  m('sprite_05', 'Sprite 0,5 L', 'ichimlik', 'dona', 'blok', 12),
  m('sprite_1', 'Sprite 1 L', 'ichimlik', 'dona', 'blok', 6),
  m('sprite_15', 'Sprite 1,5 L', 'ichimlik', 'dona', 'blok', 6),
  m('sprite_moxito_05', 'Sprite Moxito 0,5 L', 'ichimlik', 'dona', 'blok', 12),
  m('sprite_moxito_1', 'Sprite Moxito 1 L', 'ichimlik', 'dona', 'blok', 6),
  m('sprite_moxito_15', 'Sprite Moxito 1,5 L', 'ichimlik', 'dona', 'blok', 6),
  m('pepsi_05', 'Pepsi 0,5 L', 'ichimlik', 'dona', 'blok', 12),
  m('pepsi_1', 'Pepsi 1 L', 'ichimlik', 'dona', 'blok', 6),
  m('pepsi_15', 'Pepsi 1,5 L', 'ichimlik', 'dona', 'blok', 6),
  m('fuse_05', 'Fuse tea 0,5 L', 'ichimlik', 'dona', 'blok', 12),
  m('fuse_1', 'Fuse tea 1 L', 'ichimlik', 'dona', 'blok', 6),
  m('fuse_15', 'Fuse tea 1,5 L', 'ichimlik', 'dona', 'blok', 6),
  m('bonaqua_05', 'Bon Aqua 0,5 L', 'ichimlik', 'dona', 'blok', 12),
  m('suv_05', 'Suv 0,5 L', 'ichimlik', 'dona', 'blok', 12),
  m('suv_1', 'Suv 1 L', 'ichimlik', 'dona', 'blok', 6),
  m('cappy_05', 'Cappy 0,5 L', 'ichimlik', 'dona', 'blok', 12),
  m('sharbat_1', 'Sharbat 1 L', 'ichimlik', 'dona', 'blok', 6),
  m('ayran_05', 'Ayran 0,5 L', 'ichimlik', 'dona', 'blok', 12),
  m('energetik', 'Energetik ichimlik', 'ichimlik', 'dona', 'blok', 12),
  // Bar va kalyan
  m('pivo_bochka', 'Pivo (bochka)', 'bar', 'ml', 'bochka', 30000),
  m('pivo_shisha', 'Pivo 0,5 L (shisha)', 'bar', 'dona', 'yashik', 20),
  m('rom', 'Rom', 'bar', 'ml', 'shisha', 700),
  m('jin', 'Jin', 'bar', 'ml', 'shisha', 700),
  m('viski', 'Viski', 'bar', 'ml', 'shisha', 700),
  m('tonik', 'Tonik', 'bar', 'ml', 'shisha', 1000),
  m('kola_ml', 'Coca-Cola (kokteyl uchun)', 'bar', 'ml', 'shisha', 1500),
  m('shakar_sirop', 'Shakar siropi', 'bar', 'ml', 'shisha', 1000),
  m('muz', 'Muz', 'bar', 'g', 'qop', 5000),
  m('kalyan_tamaki', 'Kalyan tamakisi', 'kalyan', 'g', 'pachka', 250),
  m('kalyan_kumir', 'Kalyan ko‘miri', 'kalyan', 'dona', 'quti', 72),
  m('meva_assorti', 'Mevalar (assorti)', 'kalyan', 'g', 'kg', 1000),
  // Tayyor desertlar
  m('pirojnoe', 'Pirojnoe', 'desert', 'dona'),
  m('chizkeyk', 'Chizkeyk', 'desert', 'dona'),
];

// ---------------------------------------------------------------------------
// Taomlar
// ---------------------------------------------------------------------------
const SIZES = ['Mini', 'Standart', 'Katta'] as const;
const sizeKey = (s: string) => s.toLowerCase();

/** Bir taomning o'lchamlari: "Lavash mol go'shtli" × Mini/Standart/Katta */
function sized(keyBase: string, category: string, group: string, materials: readonly string[], sizes: readonly string[] = SIZES): LibraryItem[] {
  return sizes.map((s) => ({ key: `${keyBase}_${sizeKey(s)}`, category, name: `${group} ${s.toLowerCase()}`, group, variant: s, materials }));
}
const one = (key: string, category: string, name: string, materials: readonly string[]): LibraryItem => ({ key, category, name, materials });
const drink = (key: string, group: string, variant: string, material: string): LibraryItem => ({
  key,
  category: 'sovuq',
  name: `${group} ${variant}`,
  group,
  variant,
  materials: [material],
});

const LAVASH = ['lavash_non', 'karam', 'pomidor', 'bodring', 'oq_sous', 'tomat_sous'];
const SHAURMA = ['pita', 'karam', 'pomidor', 'bodring', 'oq_sous', 'tomat_sous'];
const HOTDOG = ['pita', 'sabzi_salat', 'karam', 'pomidor', 'bodring', 'oq_sous', 'tomat_sous'];
const FRY = ['panirovka', 'yog_fri'];

export const LIBRARY_ITEMS: readonly LibraryItem[] = [
  // Lavash
  ...sized('lavash_mol', 'lavash', 'Lavash mol go‘shtli', ['mol_goshti', ...LAVASH]),
  ...sized('lavash_tovuq', 'lavash', 'Lavash tovuq go‘shtli', ['tovuq_goshti', ...LAVASH]),
  ...sized('lavash_pishloq', 'lavash', 'Lavash pishloqli', ['mol_goshti', 'pishloq', ...LAVASH], ['Standart', 'Katta']),
  one('lavash_achchiq', 'lavash', 'Lavash achchiq', ['mol_goshti', 'achchiq_qalampir', ...LAVASH]),
  // Shaurma
  ...sized('shaurma_mol', 'shaurma', 'Shaurma mol go‘shtli', ['mol_goshti', ...SHAURMA]),
  ...sized('shaurma_tovuq', 'shaurma', 'Shaurma tovuq go‘shtli', ['tovuq_goshti', ...SHAURMA]),
  one('shaurma_pishloq', 'shaurma', 'Shaurma pishloqli', ['mol_goshti', 'pishloq', ...SHAURMA]),
  one('shaurma_tarelka', 'shaurma', 'Shaurma tarelkada', ['mol_goshti', 'kartoshka_fri', 'karam', 'pomidor', 'bodring', 'oq_sous', 'tomat_sous', 'pita']),
  // Donar
  one('donar_non', 'donar', 'Donar non ichida', ['tovuq_goshti', 'tandir_non', 'karam', 'pomidor', 'piyoz', 'oq_sous']),
  one('donar_tarelka', 'donar', 'Donar tarelkada', ['tovuq_goshti', 'kartoshka_fri', 'karam', 'pomidor', 'piyoz', 'oq_sous', 'tandir_non']),
  // Tvister va sendvich
  one('tvister', 'tvister', 'Tvister', ['tovuq_file', 'lavash_non', 'pomidor', 'salat_bargi', 'oq_sous', ...FRY]),
  one('klab_sendvich', 'tvister', 'Klab sendvich', ['tost_non', 'tovuq_file', 'pishloq', 'tuxum', 'pomidor', 'salat_bargi', 'oq_sous']),

  // Burger
  one('gamburger', 'burger', 'Gamburger', ['burger_non', 'burger_kotlet', 'pomidor', 'tuzlangan_bodring', 'piyoz', 'salat_bargi', 'ketchup', 'oq_sous']),
  one('chizburger', 'burger', 'Chizburger', ['burger_non', 'burger_kotlet', 'pishloq', 'pomidor', 'tuzlangan_bodring', 'piyoz', 'salat_bargi', 'ketchup', 'oq_sous']),
  one('dabl_burger', 'burger', 'Dabl burger', ['burger_non', 'burger_kotlet', 'pishloq', 'pomidor', 'tuzlangan_bodring', 'piyoz', 'salat_bargi', 'ketchup', 'oq_sous']),
  ...sized('chicken_burger', 'burger', 'Chicken burger', ['burger_non', 'tovuq_file', 'pomidor', 'salat_bargi', 'ketchup', 'oq_sous'], ['Mini', 'Standart']),
  one('chicken_burger_pishloq', 'burger', 'Chicken burger pishloqli', ['burger_non', 'tovuq_file', 'pishloq_sousi', 'pomidor', 'salat_bargi', 'ketchup', 'oq_sous']),
  one('big_cheese', 'burger', 'Big cheese burger', ['burger_non', 'tovuq_file', 'pishloq', 'pishloq_sousi', 'pomidor', 'salat_bargi', 'ketchup', 'oq_sous']),
  // Hot-dog
  ...sized('hotdog', 'hotdog', 'Hot-dog', ['sosiska', ...HOTDOG], ['1 sosiska', '2 sosiska', '3 sosiska']).map((i, n) => ({ ...i, key: `hotdog_${n + 1}` })),
  ...sized('hotdog_kanada', 'hotdog', 'Hot-dog Kanada', ['sosiska_kanada', ...HOTDOG], ['1 sosiska', '2 sosiska', '3 sosiska']).map((i, n) => ({
    ...i,
    key: `hotdog_kanada_${n + 1}`,
  })),
  ...sized('super_hotdog', 'hotdog', 'Super hot-dog', ['sosiska_kanada', 'kalbasa', 'tuxum', ...HOTDOG], ['O‘rtacha', 'Katta']).map((i, n) => ({
    ...i,
    key: n === 0 ? 'super_hotdog_ortacha' : 'super_hotdog_katta',
  })),
  one('fransuz_hotdog', 'hotdog', 'Fransuzcha hot-dog', ['fransuz_non', 'sosiska', 'ketchup', 'oq_sous']),
  // Chicken
  ...sized('strips', 'chicken', 'Strips', ['tovuq_file', ...FRY], ['3 dona', '5 dona']).map((i, n) => ({ ...i, key: n === 0 ? 'strips_3' : 'strips_5' })),
  ...sized('qanotcha', 'chicken', 'Qanotchalar', ['tovuq_qanot', ...FRY], ['6 dona', '12 dona']).map((i, n) => ({ ...i, key: n === 0 ? 'qanotcha_6' : 'qanotcha_12' })),
  ...sized('nagets', 'chicken', 'Nagets', ['nagets', 'yog_fri'], ['6 dona', '9 dona', '12 dona']).map((i, n) => ({ ...i, key: `nagets_${[6, 9, 12][n]}` })),
  one('tovuq_oyoqcha', 'chicken', 'Tovuq oyoqcha', ['tovuq_oyoq', ...FRY]),
  one('chicken_1kg', 'chicken', 'Chicken 1 kg', ['tovuq_file', ...FRY]),

  // Kartoshka va gazak
  ...sized('fri', 'gazak', 'Kartoshka fri', ['kartoshka_fri', 'yog_fri'], ['Standart', 'Katta']),
  one('fri_derevenski', 'gazak', 'Kartoshka po-derevenski', ['kartoshka', 'yog_fri']),
  one('fri_pishloq', 'gazak', 'Kartoshka fri pishloqli', ['kartoshka_fri', 'yog_fri', 'pishloq_sousi']),
  one('piyoz_halqa', 'gazak', 'Piyoz halqalari', ['piyoz', ...FRY]),
  one('pishloq_tayoqcha', 'gazak', 'Pishloq tayoqchalari', ['pishloq', ...FRY]),
  // Salatlar
  one('sezar', 'salat', 'Sezar salati', ['salat_bargi', 'tovuq_file', 'pomidor', 'suxari', 'parmezan', 'sezar_sous']),
  one('grek', 'salat', 'Grek salati', ['pomidor', 'bodring', 'bulgor_qalampir', 'piyoz', 'zaytun', 'brinza', 'zaytun_moyi']),
  one('achchiq_chuchuk', 'salat', 'Achchiq-chuchuk', ['pomidor', 'piyoz', 'achchiq_qalampir']),
  one('vitaminli', 'salat', 'Vitaminli salat', ['karam', 'sabzi', 'bodring']),
  // Souslar
  one('sous_ketchup', 'sous', 'Ketchup', ['ketchup', 'sous_idish']),
  one('sous_oq', 'sous', 'Oq sous', ['oq_sous', 'sous_idish']),
  one('sous_pishloq', 'sous', 'Pishloq sousi', ['pishloq_sousi', 'sous_idish']),
  one('sous_chili', 'sous', 'Chili sous', ['chili_sous', 'sous_idish']),
  one('sous_barbekyu', 'sous', 'Barbekyu sous', ['barbekyu_sous', 'sous_idish']),
  one('sous_sarimsoq', 'sous', 'Sarimsoqli sous', ['sarimsoq_sous', 'sous_idish']),

  // Sovuq ichimliklar
  drink('cola_05', 'Coca-Cola', '0,5 L', 'cola_05'),
  drink('cola_1', 'Coca-Cola', '1 L', 'cola_1'),
  drink('cola_15', 'Coca-Cola', '1,5 L', 'cola_15'),
  drink('fanta_05', 'Fanta', '0,5 L', 'fanta_05'),
  drink('fanta_1', 'Fanta', '1 L', 'fanta_1'),
  drink('fanta_15', 'Fanta', '1,5 L', 'fanta_15'),
  drink('sprite_05', 'Sprite', '0,5 L', 'sprite_05'),
  drink('sprite_1', 'Sprite', '1 L', 'sprite_1'),
  drink('sprite_15', 'Sprite', '1,5 L', 'sprite_15'),
  drink('sprite_moxito_05', 'Sprite Moxito', '0,5 L', 'sprite_moxito_05'),
  drink('sprite_moxito_1', 'Sprite Moxito', '1 L', 'sprite_moxito_1'),
  drink('sprite_moxito_15', 'Sprite Moxito', '1,5 L', 'sprite_moxito_15'),
  drink('pepsi_05', 'Pepsi', '0,5 L', 'pepsi_05'),
  drink('pepsi_1', 'Pepsi', '1 L', 'pepsi_1'),
  drink('pepsi_15', 'Pepsi', '1,5 L', 'pepsi_15'),
  drink('fuse_05', 'Fuse tea', '0,5 L', 'fuse_05'),
  drink('fuse_1', 'Fuse tea', '1 L', 'fuse_1'),
  drink('fuse_15', 'Fuse tea', '1,5 L', 'fuse_15'),
  drink('suv_05', 'Suv', '0,5 L', 'suv_05'),
  drink('suv_1', 'Suv', '1 L', 'suv_1'),
  one('bonaqua_05', 'sovuq', 'Bon Aqua 0,5 L', ['bonaqua_05']),
  one('cappy_05', 'sovuq', 'Cappy 0,5 L', ['cappy_05']),
  one('sharbat_1', 'sovuq', 'Sharbat 1 L', ['sharbat_1']),
  one('ayran_05', 'sovuq', 'Ayran 0,5 L', ['ayran_05']),
  one('energetik', 'sovuq', 'Energetik', ['energetik']),
  one('limonad', 'sovuq', 'Uy limonadi', ['limon', 'shakar', 'stakan']),
  one('moxito', 'sovuq', 'Moxito (alkogolsiz)', ['limon', 'yalpiz', 'shakar', 'stakan']),
  one('milkshake', 'sovuq', 'Milkshake', ['sut', 'muzqaymoq', 'stakan']),
  one('kompot', 'sovuq', 'Kompot', ['kompot', 'stakan']),
  // Issiq ichimliklar
  one('choy_qora', 'issiq', 'Choy (qora)', ['qora_choy', 'stakan']),
  one('choy_kok', 'issiq', 'Choy (ko‘k)', ['kok_choy', 'stakan']),
  one('choy_limon', 'issiq', 'Limon choy', ['qora_choy', 'limon', 'stakan']),
  one('kofe_31', 'issiq', 'Kofe 3 in 1', ['kofe_31', 'stakan']),
  one('kofe_qora', 'issiq', 'Qora kofe', ['kofe_qora', 'stakan']),
  one('amerikano', 'issiq', 'Amerikano', ['kofe_don', 'stakan']),
  one('kapuchino', 'issiq', 'Kapuchino', ['kofe_don', 'sut', 'stakan']),
  one('latte', 'issiq', 'Latte', ['kofe_don', 'sut', 'stakan']),
  one('kakao', 'issiq', 'Kakao', ['kakao', 'sut', 'shakar', 'stakan']),
  // Desert
  one('muzqaymoq', 'desert', 'Muzqaymoq', ['muzqaymoq']),
  one('pirojnoe', 'desert', 'Pirojnoe', ['pirojnoe']),
  one('chizkeyk', 'desert', 'Chizkeyk', ['chizkeyk']),
  // Milliy taomlar (Q-055)
  ...sized('osh', 'milliy', 'Osh', ['mol_goshti', 'guruch', 'sabzi', 'piyoz', 'noxat', 'yog_fri'], ['1 porsiya', 'Yarim']).map((i) => ({
    ...i,
    key: i.variant === 'Yarim' ? 'osh_yarim' : 'osh_1',
    name: i.variant === 'Yarim' ? 'Osh (yarim)' : 'Osh',
  })),
  one('lagmon', 'milliy', 'Lag‘mon', ['mol_goshti', 'un', 'bulgor_qalampir', 'pomidor', 'piyoz']),
  one('manti', 'milliy', 'Manti', ['qiyma', 'un', 'piyoz', 'dumba']),
  one('qozon_kabob', 'milliy', 'Qozon kabob', ['qoy_goshti', 'kartoshka', 'piyoz']),
  one('dimlama', 'milliy', 'Dimlama', ['mol_goshti', 'kartoshka', 'sabzi', 'bulgor_qalampir', 'piyoz']),
  one('norin', 'milliy', 'Norin', ['qoy_goshti', 'un', 'piyoz']),
  one('shorva', 'shorva', 'Sho‘rva', ['qoy_goshti', 'kartoshka', 'sabzi', 'piyoz', 'noxat', 'kokat']),
  one('mastava', 'shorva', 'Mastava', ['mol_goshti', 'guruch', 'kartoshka', 'sabzi', 'piyoz']),
  one('moshxorda', 'shorva', 'Moshxo‘rda', ['mol_goshti', 'mosh', 'guruch', 'sabzi']),
  one('chuchvara', 'shorva', 'Chuchvara', ['qiyma', 'un', 'piyoz']),
  one('shashlik_mol', 'shashlik', 'Shashlik mol go‘shti', ['mol_goshti', 'piyoz']),
  one('shashlik_qoy', 'shashlik', 'Shashlik qo‘y go‘shti', ['qoy_goshti', 'dumba', 'piyoz']),
  one('shashlik_tovuq', 'shashlik', 'Shashlik tovuq', ['tovuq_goshti', 'piyoz']),
  one('shashlik_jigar', 'shashlik', 'Shashlik jigar', ['jigar', 'dumba']),
  one('lyulya', 'shashlik', 'Lyulya kabob', ['qiyma', 'piyoz']),
  one('non_tandir', 'non_somsa', 'Tandir non', ['tandir_non']),
  one('somsa', 'non_somsa', 'Tandir somsa', ['qiyma', 'un', 'piyoz', 'dumba']),
  one('somsa_kartoshka', 'non_somsa', 'Kartoshkali somsa', ['kartoshka', 'un', 'piyoz']),
  // Bar va kalyan (Q-055)
  { key: 'pivo_bochka_05', category: 'pivo', name: 'Pivo bochka 0,5 L', group: 'Pivo bochka', variant: '0,5 L', materials: ['pivo_bochka'] },
  { key: 'pivo_bochka_1', category: 'pivo', name: 'Pivo bochka 1 L', group: 'Pivo bochka', variant: '1 L', materials: ['pivo_bochka'] },
  one('pivo_shisha', 'pivo', 'Pivo 0,5 L (shisha)', ['pivo_shisha']),
  one('kokteyl_moxito', 'kokteyl', 'Moxito', ['rom', 'laym', 'yalpiz', 'shakar_sirop', 'muz']),
  one('kokteyl_jin_tonik', 'kokteyl', 'Jin-tonik', ['jin', 'tonik', 'limon', 'muz']),
  one('kokteyl_viski_kola', 'kokteyl', 'Viski-kola', ['viski', 'kola_ml', 'muz']),
  one('kalyan_klassik', 'kalyan', 'Kalyan klassik', ['kalyan_tamaki', 'kalyan_kumir']),
  one('kalyan_meva', 'kalyan', 'Kalyan meva ustida', ['kalyan_tamaki', 'kalyan_kumir', 'meva_assorti']),
];

// ---------------------------------------------------------------------------
// Kutubxona rasmlari manbasi (Q-053): Lavash Fast Food Time egasi roziligi bilan (2026-10-23) uning ONE POS'dagi
// rasmlari kutubxonaga NUSXA qilinadi — restoran menyusi va fayllari o'zgarmaydi. Server faqat shu restoran
// mahsulotidan oladi (supabase/functions/library-image/source.ts — SOURCE_TENANT, test bilan bog'langan).
// Bir rasm bir nechta taomga mos (mol / tovuq go'shtli lavash — bitta rasm).
// ---------------------------------------------------------------------------
export const LIBRARY_SOURCE_RESTAURANT = { tenantId: 'e2f5cfa0-8cc3-4922-baaa-31f7aa4d279e', name: 'Lavash Fast Food Time' } as const;

const SRC = {
  lavash_mini: '61f08746-2233-4f47-b2c2-fa3e2c0df426', // Lavash Mini
  lavash_standart: 'f6142783-312d-4ae7-b9ad-6e16e94c5384', // Lavash standart
  lavash_katta: '2aa9216e-f09e-4f8b-b9b9-9f8d61a9f93f', // Lavash Katta
  shaurma_mini: '35ca6b4b-1f6d-4696-a7d4-73e45518ae32', // Shaurma Mini
  shaurma_standart: 'e25f0340-d9f5-443d-b2fa-c453bdec358f', // Shaurma standart
  shaurma_katta: 'ae6e9290-1348-4311-acd8-73bbdd4fcc26', // Shaurma Katta
  burger_mini: '764721fa-0d09-462d-89e6-8f62d3c2c747', // Burger chiken mini
  burger_big: '84df5a30-b7f3-4dcc-b6c3-80182d502adb', // Burger (big) chiken
  burger_cheese: '0c794aa0-c58d-4ad7-af3e-204fb82fa696', // Burger cheken (cheese)
  burger_big_cheese: '18e116b1-4038-4ab1-b0be-5edaa6ac6ea1', // Burger cheeken (big cheese)
  hotdog_1: '684fc8dd-4d7d-47ec-a140-97cec6c36933', // Hot Dog 1 sosiska
  hotdog_2: 'df540007-b993-445d-ac6b-d5808bde3535', // Hot Dog 2 sosiska
  hotdog_3: '31f71753-6746-4f9c-9e9a-7279bca57209', // Hot Dog 3 sosiska
  kanada_1: '04aae245-b763-47a9-a65a-40387b3f3c15', // Kanada 1 sosiska
  kanada_2: 'cf4729b0-dd29-4ae5-909b-b5d6e37fd3fb', // Kanada 2 sosiska
  kanada_3: '4a015d1c-2fad-4d34-b800-3942df6aae8e', // Kanada 3 sosiska
  super_ortacha: 'e3ccc11b-13a9-4b8e-8477-50de62b872f9', // Super Hot Dog O'rtacha
  super_katta: 'c7fc465f-6f7b-4692-a027-64a2406311bd', // Super Hot Dog Katta
  chicken_1kg: '6aa5ae3b-de0f-449a-bea6-1a36e85cf22e', // Chicken 1 kg
  fri: '9b5b36bb-130e-43fc-b3ec-7fe0f5d975b4', // Kartoshka Fri
  ketchup: 'a986fe40-10b7-40fb-bfa5-0dce2b59f404', // Ketchup
  chili: '2b9df583-f5ee-4b85-954a-19f0de17e5f5', // Chili sous
  oq_sous: 'bf5d68c8-db77-4f06-884e-72483e7367e7', // Oq sous
  pishloq_sousi: '892081d5-0031-405a-b596-2a176aea637d', // Pishloq sousi
  cola_05: '086597ef-2c35-4177-b015-2fdfb675590e', // Coca-Cola 0.5L
  cola_1: '63db12a3-c164-40ad-9039-5882684621c5', // Cola cola 1 l
  cola_15: '085da5ef-55af-4675-98d5-409e70f5476c', // coca cola 1,5 l
  fanta_05: '36fd3893-405c-4472-861a-8a90a9b10310', // Fanta 0.5L
  fanta_1: '0987e5d5-7bef-4596-8536-d7e57646979e', // Fanta 1 l
  fanta_15: '1d3affdf-d9a0-4b97-a076-e36f6d8a3f7b', // Fanta 1,5 l
  sprite_05: '3940de43-e7a7-4588-9ed9-c33e2690af7b', // Sprite 0.5L
  sprite_moxito_05: 'de2bc5dd-06b3-4563-8797-3bc65c1e4a88', // sprite (moxito) 0.5 l
  sprite_moxito_1: '56e1c269-2303-4b3a-836e-f92f6bb495a8', // Sprite moxito 1 l
  pepsi_05: '2435016c-1eed-4358-b938-ab6841872fa6', // pepsi 0.5 l
  pepsi_1: '101e4644-752f-45d2-8312-c298b759f60c', // pepsi 1 l
  pepsi_15: '8aee0669-b0b5-4b24-98a3-b9817e6c7c52', // pepsi 1,5 l
  fuse_05: '62e1a081-cdde-4010-a7ee-523df2b517bb', // fuce tee 0,5 l
  fuse_1: '9b8cff80-b234-451a-9e0c-0472a4edb696', // Fuce tee 1 l
  fuse_15: 'aff2fb3f-21a5-4706-a984-d783fd8c04db', // Fuce tee 1,5 l
  suv_05: '07ccf874-f7d5-491e-b024-0d815d53b956', // suv 0.5 l
  suv_1: '48e1d905-587d-4fac-bd60-c9c59ddfc9df', // suv 1 l
  bonaqua_05: '91804268-561b-41b6-9829-805c79d575a9', // BonAqua ( mineral) 0,5 l
  cappy_05: '862763e2-71d3-4bd7-9f93-6d22a9a4f6ab', // Pulpy (Cappy) 0.5 l
  choy: '692e82c0-b0e7-44a4-a5f0-d0d3a2d8754a', // Choy
  limon_choy: '09223938-329c-484e-91c5-debd60ff6033', // Limon choy
  kofe_31: 'c917af77-1a2b-4013-bf1c-0b763a21fa72', // Kofe 3/1
  kofe_qora: '83bed2d6-d447-48e2-ba83-537b39eea190', // Kofe Qora
  americano: '3534d13b-0685-4805-8b54-441138fc7d42', // Americano
  cappuccino: 'd257d655-0e46-4a9a-949c-bcc5661581b2', // Cappuccino
  latte: 'a325fd3f-87a2-4562-b818-2c25b88ed712', // Latte
} as const;

/** Kutubxona taomi → manba restoran mahsuloti (ID) */
export const LIBRARY_SOURCE_PRODUCTS: Readonly<Record<string, string>> = {
  lavash_mol_mini: SRC.lavash_mini,
  lavash_tovuq_mini: SRC.lavash_mini,
  lavash_mol_standart: SRC.lavash_standart,
  lavash_tovuq_standart: SRC.lavash_standart,
  lavash_mol_katta: SRC.lavash_katta,
  lavash_tovuq_katta: SRC.lavash_katta,
  shaurma_mol_mini: SRC.shaurma_mini,
  shaurma_tovuq_mini: SRC.shaurma_mini,
  shaurma_mol_standart: SRC.shaurma_standart,
  shaurma_tovuq_standart: SRC.shaurma_standart,
  shaurma_mol_katta: SRC.shaurma_katta,
  shaurma_tovuq_katta: SRC.shaurma_katta,
  chicken_burger_mini: SRC.burger_mini,
  chicken_burger_standart: SRC.burger_big,
  chicken_burger_pishloq: SRC.burger_cheese,
  big_cheese: SRC.burger_big_cheese,
  hotdog_1: SRC.hotdog_1,
  hotdog_2: SRC.hotdog_2,
  hotdog_3: SRC.hotdog_3,
  hotdog_kanada_1: SRC.kanada_1,
  hotdog_kanada_2: SRC.kanada_2,
  hotdog_kanada_3: SRC.kanada_3,
  super_hotdog_ortacha: SRC.super_ortacha,
  super_hotdog_katta: SRC.super_katta,
  chicken_1kg: SRC.chicken_1kg,
  fri_standart: SRC.fri,
  fri_katta: SRC.fri,
  sous_ketchup: SRC.ketchup,
  sous_chili: SRC.chili,
  sous_oq: SRC.oq_sous,
  sous_pishloq: SRC.pishloq_sousi,
  cola_05: SRC.cola_05,
  cola_1: SRC.cola_1,
  cola_15: SRC.cola_15,
  fanta_05: SRC.fanta_05,
  fanta_1: SRC.fanta_1,
  fanta_15: SRC.fanta_15,
  sprite_05: SRC.sprite_05,
  sprite_moxito_05: SRC.sprite_moxito_05,
  sprite_moxito_1: SRC.sprite_moxito_1,
  pepsi_05: SRC.pepsi_05,
  pepsi_1: SRC.pepsi_1,
  pepsi_15: SRC.pepsi_15,
  fuse_05: SRC.fuse_05,
  fuse_1: SRC.fuse_1,
  fuse_15: SRC.fuse_15,
  suv_05: SRC.suv_05,
  suv_1: SRC.suv_1,
  bonaqua_05: SRC.bonaqua_05,
  cappy_05: SRC.cappy_05,
  choy_qora: SRC.choy,
  choy_limon: SRC.limon_choy,
  kofe_31: SRC.kofe_31,
  kofe_qora: SRC.kofe_qora,
  amerikano: SRC.americano,
  kapuchino: SRC.cappuccino,
  latte: SRC.latte,
};

// ---------------------------------------------------------------------------
const MATERIAL_MAP: ReadonlyMap<string, LibraryMaterial> = new Map(LIBRARY_MATERIALS.map((x) => [x.code, x]));
const CATEGORY_MAP: ReadonlyMap<string, LibraryCategory> = new Map((LIBRARY_CATEGORIES as readonly LibraryCategory[]).map((c) => [c.key, c]));

export function libraryMaterial(code: string): LibraryMaterial | undefined {
  return MATERIAL_MAP.get(code);
}
export function libraryCategory(key: string): LibraryCategory | undefined {
  return CATEGORY_MAP.get(key);
}

// ---------------------------------------------------------------------------
// Guruh rasmi va kategoriya fon rasmi (Q-054): kutubxonada "grp_<guruh>" va "cat_<kategoriya>" kalitlari
// ---------------------------------------------------------------------------

/** "Coca-Cola" → "grp_coca_cola"; "Lavash mol go‘shtli" → "grp_lavash_mol_goshtli" (kalit o'zgarmaydi) */
export function libraryGroupKey(group: string): string {
  const slug = group
    .toLowerCase()
    .replace(/[‘’'ʻʼ`]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
  return `grp_${slug}`;
}

export function libraryCategoryImageKey(category: string): string {
  return `cat_${category}`;
}

export interface LibraryGroup {
  key: string;
  name: string;
  category: string;
  items: readonly LibraryItem[];
}

/** Kutubxonadagi o'lchamli guruhlar (kategoriya tartibida) */
export const LIBRARY_GROUPS: readonly LibraryGroup[] = (() => {
  const out = new Map<string, { key: string; name: string; category: string; items: LibraryItem[] }>();
  for (const i of LIBRARY_ITEMS) {
    if (!i.group) continue;
    const key = libraryGroupKey(i.group);
    const g = out.get(key) ?? { key, name: i.group, category: i.category, items: [] };
    g.items.push(i);
    out.set(key, g);
  }
  return [...out.values()];
})();

/** Server (mgr_library_add) uchun yuk: tanlangan taomlar, ularning kategoriyalari va xom ashyolari */
export interface LibraryPayload {
  categories: { key: string; name: string; icon: string }[];
  materials: { code: string; name: string; category: string; base_unit: LibraryUnit; pack_name: string | null; pack_size: number | null }[];
  items: {
    key: string;
    category: string;
    name: string;
    group_name: string | null;
    /** Guruhning kutubxona rasmi kaliti (Q-054): "grp_coca_cola" */
    group_key: string | null;
    variant_label: string | null;
    price: number;
    materials: string[];
  }[];
}

export function buildLibraryPayload(keys: readonly string[], prices: Readonly<Record<string, number>> = {}): LibraryPayload {
  const picked = new Set(keys);
  const items = LIBRARY_ITEMS.filter((i) => picked.has(i.key));
  const catKeys = [...new Set(items.map((i) => i.category))];
  const codes = [...new Set(items.flatMap((i) => i.materials))];
  return {
    categories: catKeys.map((k) => {
      const c = CATEGORY_MAP.get(k)!;
      return { key: c.key, name: c.name, icon: c.icon };
    }),
    materials: codes.map((code) => {
      const x = MATERIAL_MAP.get(code)!;
      return { code: x.code, name: x.name, category: x.category, base_unit: x.unit, pack_name: x.packName ?? null, pack_size: x.packSize ?? null };
    }),
    items: items.map((i) => ({
      key: i.key,
      category: i.category,
      name: i.name,
      group_name: i.group ?? null,
      group_key: i.group ? libraryGroupKey(i.group) : null,
      variant_label: i.variant ?? null,
      price: Math.max(0, Math.round(prices[i.key] ?? 0)),
      materials: [...i.materials],
    })),
  };
}
